/**
 * Undo/Redo MVI Reducer
 *
 * Architecture: MVI Pattern - Reducer Layer
 * Pure function: no I/O, no side effects.
 */

import type { IUndoRedoState } from "./state";
import type { TUndoRedoIntent } from "./intents";

// ============================================================================
// Reducer
// ============================================================================

export function undoRedoReducer(
  state: IUndoRedoState,
  intent: TUndoRedoIntent,
): IUndoRedoState {
  switch (intent.type) {
    case 'SessionInitStarted':
      return {
        ...state,
        ui: { ...state.ui, isInitializing: true },
      };

    case 'SessionInitCompleted':
      return {
        domain: {
          ...state.domain,
          sessionId: intent.sessionId,
          clientId: intent.clientId,
          sessionProjectId: intent.projectId,
          canUndo: intent.canUndo,
          canRedo: intent.canRedo,
        },
        ui: { ...state.ui, isInitializing: false },
      };

    case 'SessionInitFailed':
      return {
        ...state,
        ui: { ...state.ui, isInitializing: false },
      };

    case 'UndoStarted':
    case 'RedoStarted':
      // Прежний отказ снимается вместе с началом новой операции: показанным он
      // уже был, а ждать показа второй раз ему незачем.
      return {
        ...state,
        ui: { ...state.ui, isProcessing: true, pendingFailure: null },
      };

    case 'UndoCompleted':
    case 'RedoCompleted':
    // Отмена из истории — то же событие для страниц: версия растёт, данные
    // перечитываются (change-history-R15, undo-redo-R12).
    case 'ExternalGroupApplied':
      return {
        domain: {
          ...state.domain,
          canUndo: intent.canUndo,
          canRedo: intent.canRedo,
          lastActionVersion: state.domain.lastActionVersion + 1,
        },
        ui: { ...state.ui, isProcessing: false, pendingFailure: null },
      };

    // Признак обработки снимается в ЛЮБОМ исходе, а причина отказа кладётся в
    // состояние для показа пользователю: молчаливое гашение недопустимо —
    // нажавший «Отменить» обязан узнать, почему ничего не изменилось
    // (undo-redo-R19). Версия последнего действия при неудаче не растёт
    // (undo-redo-R12), поэтому страницы данные не перечитывают.
    case 'UndoFailed':
    case 'RedoFailed':
      return {
        ...state,
        ui: { ...state.ui, isProcessing: false, pendingFailure: intent.error },
      };

    case 'FailureShown':
      return {
        ...state,
        ui: { ...state.ui, pendingFailure: null },
      };

    case 'StateUpdated':
      return {
        ...state,
        domain: {
          ...state.domain,
          canUndo: intent.canUndo,
          canRedo: intent.canRedo,
        },
      };

    case 'FeatureSelectionChanged':
      return {
        ...state,
        domain: {
          ...state.domain,
          hasSelectedFeature: intent.hasSelectedFeature,
          isFeatureOfSelectedProject: intent.belongsToSelectedProject,
        },
      };

    case 'ProjectSelectionChanged':
      return {
        ...state,
        domain: {
          ...state.domain,
          selectedProjectId: intent.projectId,
        },
      };

    case 'SessionClosed':
      return {
        domain: {
          ...state.domain,
          sessionId: null,
          clientId: null,
          sessionProjectId: null,
          canUndo: false,
          canRedo: false,
        },
        ui: {
          ...state.ui,
          isProcessing: false,
          isInitializing: false,
          pendingFailure: null,
        },
      };

    default:
      return state;
  }
}
